import React, { useState, useCallback, useRef } from "react";
import { View, StyleSheet, Pressable, Animated } from "react-native";
import Pill from "../../common/Pill/Pill";
import CustomText from "../../common/general/CustomText/CustomText";
import { CourseGenerationStatus } from "../../../graphql/generated/graphql";
import { formatDateToSeason } from "../../../utils/date";
import { MoreVertical } from "lucide-react";

interface Course {
  id: string;
  name?: string | null;
  course_code?: string | null;
  description?: string | null;
  startDate?: string | null;
  isDraft?: boolean | null;
  generationStatus?: CourseGenerationStatus | null;
  last_modified?: string | null;
  enrolledStudents?: Array<{
    id: string;
    name: string;
  }> | null;
  modules: Array<{
    id: string;
    title?: string;
    aiTutors: Array<{
      id: string;
      title: string;
    }>;
    assignments: Array<{
      id: string;
      name?: string;
    }>;
    axioResources: Array<{
      id: string;
      name?: string;
    }>;
    reviews?: Array<{
      id: string;
    }>;
  }>;
}

interface TeachingCardProps {
  course: Course;
  onCardPress: (courseId: string) => void;
}

const TeachingCard: React.FC<TeachingCardProps> = ({ course, onCardPress }) => {
  const [isHovered, setIsHovered] = useState(false);

  const formatCourseNameWithCode = (courseName: string, courseCode?: string | null): string => {
    if (courseCode && courseCode.trim()) {
      return `${courseCode} ${courseName}`;
    }
    return courseName;
  };

  const formatLastUpdated = (dateString: string | null) => {
    if (!dateString) return "Never updated";
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Updated today";
    if (diffDays === 1) return "Updated 1 day ago";
    if (diffDays < 7) return `Updated ${diffDays} days ago`;
    if (diffDays < 14) return "Updated 1 week ago";
    if (diffDays < 30) return `Updated ${Math.floor(diffDays / 7)} weeks ago`;
    return `Updated ${Math.floor(diffDays / 30)} months ago`;
  };

  const getUpdateDotColor = (dateString: string | null) => {
    if (!dateString) return "#64748B"; // Gray for never updated

    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays <= 2) {
      return "#3b7c0f"; // Green for last 2 days
    } else if (diffDays <= 7) {
      return "#b54708"; // Amber/Orange for 3 days to 1 week
    } else {
      return "#667085"; // Gray for over 1 week
    }
  };

  const getCourseStatusPill = (course: Course) => {
    if (course.isDraft) {
      return { text: "Draft", backgroundColor: "#F2F4F7", textColor: "#344054", borderColor: "#eaecf0" };
    }
    return { text: "Published", backgroundColor: "#F3FEE7", textColor: "#3B7C0f", borderColor: "#e3fbcc" };
  };

  const getActivityCount = (modules: Course["modules"]) => {
    return modules.reduce((total, module) => {
      return (
        total +
        module.aiTutors.length +
        module.assignments.length +
        module.axioResources.length +
        (module.reviews?.length || 0)
      );
    }, 0);
  };

  const handleCardPress = useCallback(() => {
    onCardPress(course.id);
  }, [course.id, onCardPress]);

  const statusPill = getCourseStatusPill(course);
  const studentCount = course.enrolledStudents?.length || 0;
  const moduleCount = course.modules.length;
  const activityCount = getActivityCount(course.modules);
  const displayName = formatCourseNameWithCode(course.name, course.course_code);
  const scale = useRef(new Animated.Value(1)).current;
  const translateY = useRef(new Animated.Value(0)).current;

  const handleHoverIn = () => {
    Animated.parallel([
      Animated.spring(scale, {
        toValue: 1.02,
        useNativeDriver: true,
      }),
      Animated.spring(translateY, {
        toValue: -4,
        useNativeDriver: true,
      }),
    ]).start();
    setIsHovered(true);
  };

  const handleHoverOut = () => {
    Animated.parallel([
      Animated.spring(scale, {
        toValue: 1,
        useNativeDriver: true,
      }),
      Animated.spring(translateY, {
        toValue: 0,
        useNativeDriver: true,
      }),
    ]).start();
    setIsHovered(false);
  };

  const handlePressIn = () => {
    Animated.spring(scale, {
      toValue: 0.98,
      useNativeDriver: true,
    }).start();
  };

  const handlePressOut = () => {
    Animated.spring(scale, {
      toValue: 1,
      useNativeDriver: true,
    }).start();
  };

  const shadowStyle = isHovered
    ? {
        shadowColor: "#000",
        shadowOffset: {
          width: 0,
          height: 12,
        },
        shadowOpacity: 0.12,
        shadowRadius: 16,
        elevation: 8,
      }
    : {};

  return (
    <>
      <Animated.View
        style={[
          {
            transform: [{ scale: scale }, { translateY: translateY }],
            borderRadius: 32,
          },
          shadowStyle,
        ]}>
        <Pressable
          testID={`teaching-course-card-${course.id}`}
          style={[styles.card, { padding: 24, borderRadius: 32 }]}
          onPress={handleCardPress}
          onHoverIn={handleHoverIn}
          onHoverOut={handleHoverOut}
          onPressIn={handlePressIn}
          onPressOut={handlePressOut}>
          <View style={styles.cardHeader}>
            {course?.hasNotification && (
              <View
                style={{
                  position: "absolute",
                  top: -8,
                  right: -8,
                  width: 8,
                  height: 8,
                  backgroundColor: "#3459D4",
                  borderRadius: 999,
                }}
              />
            )}
            <View style={styles.pillContainer}>
              <Pill
                text={statusPill.text[0].toUpperCase() + statusPill.text.slice(1)}
                backgroundColor={statusPill.backgroundColor}
                textColor={statusPill.textColor}
                textSize="xs"
                updated={true}
                borderColor={statusPill.borderColor}
                borderWidth={1}
              />
            </View>
            <View>{isHovered && <MoreVertical style={{ color: "#344054", height: 16, width: 16 }} />}</View>
          </View>

          <View style={{ marginBottom: 16 }}>
            <CustomText
              text={course.name}
              size="xl"
              weight="bold"
              useTranslationText={false}
              style={[styles.courseName, { marginBottom: 4 }, isHovered ? { color: "#3459D4" } : {}]}
              numberOfLines={2}
              ellipsizeMode="tail"
            />

            <CustomText
              text={
                course.course_code && course.course_code.trim()
                  ? `${course.course_code} • ${formatDateToSeason(course.created)}`
                  : formatDateToSeason(course.created)
              }
              weight="normal"
              size="s"
              useTranslationText={false}
              style={{ color: "#344054", marginBottom: 12 }}
            />

            <CustomText
              text={course.description || "No description available"}
              size="s"
              weight="normal"
              useTranslationText={false}
              style={{ color: "#344054" }}
              numberOfLines={2}
              ellipsizeMode="tail"
            />
          </View>

          <View style={styles.statsContainer}>
            <View style={styles.statItem}>
              <CustomText
                text={studentCount.toString()}
                size="xxl"
                weight="ultraBold"
                useTranslationText={false}
                style={styles.statNumber}
              />
              <CustomText
                text="Students"
                size="xs"
                weight="normal"
                useTranslationText={false}
                style={[styles.statLabel, { color: "#344054" }]}
              />
            </View>

            <View style={styles.statItem}>
              <CustomText
                text={moduleCount.toString()}
                size="xxl"
                weight="ultraBold"
                useTranslationText={false}
                style={styles.statNumber}
              />
              <CustomText
                text="Modules"
                size="xs"
                weight="normal"
                useTranslationText={false}
                style={[styles.statLabel, { color: "#344054" }]}
              />
            </View>

            <View style={styles.statItem}>
              <CustomText
                text={activityCount.toString()}
                size="xxl"
                weight="ultraBold"
                useTranslationText={false}
                style={styles.statNumber}
              />
              <CustomText
                text="Activities"
                size="xs"
                weight="normal"
                useTranslationText={false}
                style={[styles.statLabel, { color: "#344054" }]}
              />
            </View>
          </View>

          <View style={styles.footer}>
            <View style={styles.updateIndicator}>
              <View style={[styles.updateDot, { backgroundColor: getUpdateDotColor(course.last_modified) }]} />
              <CustomText
                size="s"
                text={formatLastUpdated(course.last_modified)}
                weight="medium"
                useTranslationText={false}
                style={{ color: "#344054" }}
              />
            </View>
          </View>
        </Pressable>
      </Animated.View>
    </>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 2,
    minHeight: 322,
  },
  cardHovered: {
    borderColor: "#3B82F6",
    shadowColor: "#3B82F6",
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  cardHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  pillContainer: {
    alignSelf: "flex-start",
  },
  courseName: {
    color: "#101828",
    lineHeight: 28,
  },
  courseCode: {
    color: "#64748B",
  },
  description: {
    color: "#64748B",
    lineHeight: 24,
  },
  statsContainer: {
    flexDirection: "row",
    gap: 24,
    alignItems: "center",
    paddingBottom: 16,
    borderBottomWidth: 0.8,
    borderColor: "#EAECF0",
    marginBottom: 16,
  },
  statItem: {
    alignItems: "center",
  },
  statNumber: {
    color: "#101828",
  },
  statLabel: {
    letterSpacing: 0.5,
    marginTop: 4,
  },
  footer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
  },
  updateIndicator: {
    flexDirection: "row",
    alignItems: "center",
  },
  updateDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginRight: 8,
  },
  updateText: {
    color: "#64748B",
    fontSize: 13,
  },
});

export default TeachingCard;
